import { useEffect, useState } from "react";
import { X, Trash2 } from "lucide-react";
import { useLang } from "@/lib/adminLang";
import { SCEN, CHANNELS } from "./budsjettUtils";

export default function TicketModal({ ticket, presetChannel, computed, onSave, onDelete, onClose }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: ticket?.name || "",
    channel: ticket?.channel || presetChannel || "physical",
    price: ticket?.price ?? 0,
    capacity: ticket?.capacity ?? 0,
    feesPct: ticket?.feesPct ?? 4,
    vatPct: ticket?.vatPct ?? 25,
    sellThrough: {
      downside: ticket?.sellThrough?.downside ?? 40,
      base: ticket?.sellThrough?.base ?? 70,
      upside: ticket?.sellThrough?.upside ?? 90,
    },
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const inv = computed?.inventory;
  const hint =
    form.channel === "physical" && inv
      ? `${t("Physical inventory")}: ${inv.physical.sellable} ${t("sellable")} · ${inv.physical.allocated} ${t("allocated")}`
      : form.channel === "dinner" && inv
        ? `${t("Dinner inventory:")} ${inv.dinner.sellable} ${t("sellable add-ons")} · ${inv.dinner.allocated} ${t("allocated")}`
        : t("Digital is a soft pool — no venue constraint.");

  const save = async () => {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        price: Number(form.price) || 0,
        capacity: Number(form.capacity) || 0,
        feesPct: Number(form.feesPct) || 0,
        vatPct: Number(form.vatPct) || 0,
        sellThrough: Object.fromEntries(Object.entries(form.sellThrough).map(([k, v]) => [k, Number(v) || 0])),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="ticket-modal">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onClose} />
      <aside className="sak-drawer relative w-full max-w-[480px] h-full bg-white shadow-2xl border-l border-[#E7E4DB] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 shrink-0">
          <h2 className="text-[15px] font-semibold">{ticket ? t("Edit ticket product") : t("New ticket product")}</h2>
          <div className="flex items-center gap-1">
            {onDelete && (
              <button type="button" onClick={onDelete} className="p-1.5 text-neutral-400 hover:text-red-600 transition-colors" data-testid="ticket-delete-btn">
                <Trash2 size={14} />
              </button>
            )}
            <button type="button" onClick={onClose} className="p-1.5 text-neutral-400 hover:text-neutral-800 transition-colors" data-testid="ticket-close-btn">
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="grid grid-cols-2 gap-3">
          <label className="adm-label col-span-2">
            {t("Name")}
            <input value={form.name} onChange={(e) => set("name", e.target.value)} className="adm-input" data-testid="ticket-name-input" />
          </label>
          <label className="adm-label">
            {t("Channel")}
            <select value={form.channel} onChange={(e) => set("channel", e.target.value)} className="adm-input" data-testid="ticket-channel-select">
              {CHANNELS.map((c) => (
                <option key={c.k} value={c.k}>{t(c.label)}</option>
              ))}
            </select>
          </label>
          <label className="adm-label">
            {t("Price ex VAT")}
            <input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="adm-input" data-testid="ticket-price-input" />
          </label>
          <label className="adm-label">
            {t("Capacity")}
            <input type="number" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} className="adm-input" data-testid="ticket-capacity-input" />
          </label>
          <label className="adm-label">
            {t("Fees % (ticketing/payment)")}
            <input type="number" value={form.feesPct} onChange={(e) => set("feesPct", e.target.value)} className="adm-input" data-testid="ticket-fees-input" />
          </label>
          <label className="adm-label">
            {t("VAT %")}
            <input type="number" value={form.vatPct} onChange={(e) => set("vatPct", e.target.value)} className="adm-input" />
          </label>
        </div>
        <p className="text-[10.5px] text-neutral-400 mt-2" data-testid="ticket-inventory-hint">{hint}</p>
        <p className="adm-label mt-4 mb-2">{t("Sell-through per scenario (%)")}</p>
        <div className="grid grid-cols-3 gap-3">
          {SCEN.map((sc) => (
            <label key={sc.k} className="adm-label">
              {sc.label}
              <input
                type="number"
                min={0}
                max={100}
                value={form.sellThrough[sc.k]}
                onChange={(e) => setForm((f) => ({ ...f, sellThrough: { ...f.sellThrough, [sc.k]: e.target.value } }))}
                className="adm-input"
                data-testid={`ticket-st-${sc.k}`}
              />
            </label>
          ))}
        </div>
        </div>
        <div className="shrink-0 px-6 py-3 border-t border-neutral-100 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="adm-btn-ghost">{t("Cancel")}</button>
          <button type="button" onClick={save} disabled={!form.name.trim() || saving} className="adm-btn" data-testid="ticket-save-btn">
            {saving ? t("Saving …") : t("Save")}
          </button>
        </div>
      </aside>
    </div>
  );
}
